import { Button, Checkbox, Popover } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import css from "./QuickFilterSelect.module.scss";

// Gap between the trigger and the dropdown, in px (Popper takes a number).
const DROPDOWN_OFFSET = 8;

export interface QuickFilterOption {
    value: string;
    label: string;
    count: number;
}

/**
 * Multi-select facet for a table ribbon. An empty selection means "no
 * filter", so callers can treat it as pass-everything rather than
 * hide-everything.
 */
export const QuickFilterSelect: React.FC<{
    label: string;
    options: ReadonlyArray<QuickFilterOption>;
    selected: ReadonlySet<string>;
    onChange: (selected: ReadonlySet<string>) => void;
}> = ({ label, options, selected, onChange }) => {
    const isDirty = selected.size > 0;
    const isEverythingSelected =
        options.length > 0 && options.every((o) => selected.has(o.value));

    const toggleOne = (value: string) => {
        const next = new Set(selected);
        if (next.has(value)) {
            next.delete(value);
        } else {
            next.add(value);
        }
        onChange(next);
    };

    const popoverContent = (
        <div className={css["QuickFilterSelect-popover"]}>
            <div className={css["QuickFilterSelect-popover-header"]}>
                <span className={css["QuickFilterSelect-popover-title"]}>
                    {label}
                </span>
                <Button
                    variant="minimal"
                    size="small"
                    intent="primary"
                    disabled={isEverythingSelected}
                    onClick={() =>
                        onChange(new Set(options.map((o) => o.value)))
                    }
                >
                    All
                </Button>
                <Button
                    variant="minimal"
                    size="small"
                    intent="primary"
                    disabled={!isDirty}
                    onClick={() => onChange(new Set())}
                >
                    None
                </Button>
            </div>
            {options.map((option) => (
                <Checkbox
                    key={option.value}
                    className={css["QuickFilterSelect-popover-row"]}
                    checked={selected.has(option.value)}
                    onChange={() => toggleOne(option.value)}
                >
                    <span className={css["QuickFilterSelect-popover-label"]}>
                        {option.label}
                    </span>
                    <span className={css["QuickFilterSelect-popover-count"]}>
                        {option.count}
                    </span>
                </Checkbox>
            ))}
        </div>
    );

    return (
        <Popover
            content={popoverContent}
            placement="bottom-start"
            popoverClassName={css["QuickFilterSelect-dropdown"]}
            modifiers={{
                offset: {
                    enabled: true,
                    options: { offset: [0, DROPDOWN_OFFSET] }
                }
            }}
            minimal
        >
            <Button
                className={css["QuickFilterSelect-button"]}
                endIcon={IconNames.CHEVRON_DOWN}
                intent={isDirty ? "primary" : "none"}
                variant="outlined"
            >
                {label}
                <span
                    className={css["QuickFilterSelect-badge"]}
                    // Always laid out, so applying a filter cannot resize the
                    // trigger and shuffle the controls beside it.
                    style={{ visibility: isDirty ? "visible" : "hidden" }}
                >
                    {selected.size}
                </span>
            </Button>
        </Popover>
    );
};
